import React from "react";
import { useTranslation } from "react-i18next";
import { useColleagues } from "../../instructors/hooks/useColleagues";

interface MemberInstructorSelectProps {
  /** Assigned instructor id; null = unassigned. */
  value: string | null;
  onChange: (instructorId: string | null) => void;
  /**
   * Offer the "Unassigned" option. Only meaningful while the member is still
   * unassigned — the assign endpoint has no way back to unassigned.
   */
  allowUnassigned: boolean;
  disabled?: boolean;
}

/**
 * Optional "Instructor" assignment select on the member form. Renders only in
 * a multi-instructor organization (solo studios never see it).
 */
const MemberInstructorSelect: React.FC<MemberInstructorSelectProps> = ({
  value,
  onChange,
  allowUnassigned,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { colleagues, activeColleagues, isMultiInstructor } = useColleagues();

  if (!isMultiInstructor) {
    return null;
  }

  // Keep a since-deactivated assigned instructor selectable so opening the
  // form doesn't silently reassign the member.
  const assignedInactive = colleagues.find(
    (c) => c.id === value && !c.isActive
  );
  const options = assignedInactive
    ? [...activeColleagues, assignedInactive]
    : activeColleagues;

  return (
    <div className="w-full">
      <label htmlFor="member_instructor" className="label">
        {t("members.instructorSelect.label", "Instructor")}
      </label>
      <select
        id="member_instructor"
        name="member_instructor"
        className="input w-full"
        value={value ?? ""}
        onChange={(e) =>
          onChange(e.target.value === "" ? null : e.target.value)
        }
        disabled={disabled}
      >
        {allowUnassigned && (
          <option value="">{t("members.instructorSelect.unassigned", "Unassigned")}</option>
        )}
        {options.map((instructor) => (
          <option key={instructor.id} value={instructor.id}>
            {instructor.fullName}
            {!instructor.isActive
              ? ` ${t("members.instructorSelect.inactiveSuffix", "(inactive)")}`
              : ""}
          </option>
        ))}
      </select>
    </div>
  );
};

export default MemberInstructorSelect;
